import { FC, useEffect, useState } from "react";
import SectionFounder from "./SectionFounder";
import SectionStatistic from "./SectionStatistic";
import { Helmet } from "react-helmet";
import SectionSubscribe2 from "components/SectionSubscribe2/SectionSubscribe2";
import BgGlassmorphism from "components/BgGlassmorphism/BgGlassmorphism";
import BackgroundSection from "components/BackgroundSection/BackgroundSection";
import SectionHero from "./SectionHero";
import { useParams } from "react-router-dom";
import { useGetBorgoQuery, useGetBorgoSectionsQuery } from "application/features/borghi/borghiApiSlice";
import { useLazyGetByCityQuery } from "application/features/search/searchApiSlice";
import { useLazyGetExperienceByCityQuery } from "application/features/experience/experienceApiSlice";
import { useLazyGetHolidayPackageByCityQuery } from "application/features/holidayPackage/holidayPackageApiSlice";
import { TESTIMONIALS_DATA } from "data/site_content";
import { useTranslation } from "react-i18next";
import MultiStockCard from "components/Quasi/MultiStockCard/MultiStockCard";
import { MoodEnum, StockNumericType } from "data/enums";
import { stockDetailPath } from "data/stock_types";
import Heading from "shared/Heading/Heading";
import Skeleton from "react-loading-skeleton";
import ButtonSecondary from "shared/Button/ButtonSecondary";

export interface PageBorgoProps {
  className?: string;
}

const PageBorgo: FC<PageBorgoProps> = ({ className = "" }) => {

  const { t, ready } = useTranslation("translation");

  const [showAllAmenities, setShowAllAmenities] = useState(false);
  const [showAllProperties, setShowAllProperties] = useState(false);
  const [showAllExperiences, setShowAllExperiences] = useState(false);
  const [showAllPackages, setShowAllPackages] = useState(false);
  const { name } = useParams();
  const { data, isLoading } = useGetBorgoQuery(name);
  const { data: sections, isLoading: isLoadingSections } = useGetBorgoSectionsQuery(name);

  const [filterProperties, { data: properties, isLoading: isLoadingProperties }] = useLazyGetByCityQuery({});
  const [filterExperiences, { data: experiences, isLoading: isLoadingExperiences }] = useLazyGetExperienceByCityQuery({});
  const [filterHolidayPackages, { data: holidayPackages, isLoading: isLoadingHolidayPackages }] = useLazyGetHolidayPackageByCityQuery({});

  useEffect(() => {
    if (name) {
      filterProperties({ city: name });
      filterExperiences({ city: name });
      filterHolidayPackages({ city: name });
    }
  }, [name]);

  if (!ready) return null;

  const translatedTestimonials = t("translation:home.testimonials.list", { returnObjects: true }) as any[];
  const assembledTestimonials = TESTIMONIALS_DATA.map(
    (item, index) => {
      return {
        id: index,
        avatar: item,
        name: translatedTestimonials[index].name,
        title: translatedTestimonials[index].title,
        text: translatedTestimonials[index].text,
      }
    }
  );

  function capitalizeFirstLetter(string: string) {
    return string.charAt(0).toUpperCase() + string.slice(1);
  }

  const renderPropertyCard = (stay: any) => {
    const galleryImages = [stay.cover, ...(stay.gallery_images || [])].filter((url: any) => url);
    return (
      <MultiStockCard
        key={stay.property_uuid}
        id={stay.property_uuid}
        title={stay.name}
        cover={stay.cover}
        images={galleryImages}
        address={stay.address}
        price={stay.weekday_price}
        mood={stay.mood as MoodEnum}
        rating={stay.rating}
        reviewCount={stay.reviews}
        max_guests={stay.max_guests}
        annotation={stay.property_type}
        href={stockDetailPath(StockNumericType.STAY, stay.slug || stay.property_uuid)}
        orientation="vertical"
      />
    );
  };

  const renderExperienceCard = (experience: any) => {
    const galleryImages = [experience.cover, ...(experience.media || [])].filter((url: any) => url);
    return (
      <MultiStockCard
        key={experience.experience_uuid}
        id={experience.experience_uuid}
        title={experience.name}
        cover={experience.cover}
        images={galleryImages}
        address={experience.address}
        price={experience.price}
        rating={experience.rating}
        reviewCount={experience.reviews}
        max_guests={experience.max_guests}
        minutes_duration={experience.minutes_duration}
        href={stockDetailPath(StockNumericType.EXPERIENCE, experience.slug || experience.experience_uuid)}
        orientation="vertical"
      />
    );
  };

  const renderHolidayPackageCard = (pkg: any) => {
    const galleryImages = [pkg.cover || pkg.cover_image, ...(pkg.gallery_images || [])].filter((url: any) => url);
    return (
      <MultiStockCard
        key={pkg.package_uuid}
        id={pkg.package_uuid}
        title={pkg.name}
        cover={pkg.cover || pkg.cover_image}
        images={galleryImages}
        address={pkg.address}
        price={pkg.price}
        rating={pkg.rating}
        reviewCount={pkg.reviews}
        max_guests={pkg.max_guests}
        href={stockDetailPath(StockNumericType.HOLIDAY_PACKAGE, pkg.slug || pkg.package_uuid)}
        orientation="vertical"
      />
    );
  };

  const renderAmenities = () => {
    if (!data?.borgo?.amenities?.length) return null;

    const displayedAmenities = showAllAmenities
      ? data.borgo.amenities
      : data.borgo.amenities.slice(0, 5);

    return (
      <div className="container relative space-y-8 mb-24 lg:space-y-10">
        <div className="relative py-16">
          <h2 className="text-2xl font-semibold">Servizi disponibili nella zona</h2>
          <div className="w-14 border-b border-neutral-200 dark:border-neutral-700 mt-4"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
            {displayedAmenities.map((amenity: { amenity: string }, index: number) => (
              <div
                key={index}
                className="flex items-center p-4 bg-neutral-50 dark:bg-neutral-800 rounded-lg"
              >
                <span className="text-neutral-600 dark:text-neutral-300">
                  {amenity.amenity}
                </span>
              </div>
            ))}
          </div>
          {data.borgo.amenities.length > 5 && (
            <div className="flex justify-center mt-8">
              <ButtonSecondary
                onClick={() => setShowAllAmenities(!showAllAmenities)}
              >
                {showAllAmenities ? "Mostra meno" : `Mostra tutti i ${data.borgo.amenities.length} servizi`}
              </ButtonSecondary>
            </div>
          )}
        </div>
      </div>
    );
  };

  if (isLoading) {
    return <Skeleton height={500} />;
  }

  return (
    <div
      className={`nc-PageBorgo overflow-hidden relative ${className}`}
      data-nc-id="PageBorgo"
    >
      <Helmet>
        <title>{data?.borgo?.name || "Borgo"} || TRIBYOU</title>
      </Helmet>

      {/* ======== BG GLASS ======== */}
      <BgGlassmorphism />

      <div className="container py-16 lg:py-28 space-y-16 lg:space-y-28">
        <SectionHero
          rightImg={data?.borgo?.cover || ""}
          heading={data?.borgo?.name && capitalizeFirstLetter(data.borgo.name) || ""}
          btnText=""
          subHeading={data?.borgo?.short_description || ""}
          verified={data?.borgo?.verified}
        />
        {(data?.borgo?.description && data?.borgo?.description.length > 30) &&
          <div className="relative py-16">
            <Heading
              desc={``}
            >
              {`${t("translation:borghi.borgoPage.discoverAbout")} ${data?.borgo?.name}`}
            </Heading>
            <div className="text-neutral-900 dark:text-neutral-100 text-lg">
              <p className="text-lg text-neutral-500 dark:text-neutral-400 text-justify">
                {data?.borgo?.description || ""}
              </p>
            </div>
          </div>
        }
        {renderAmenities()}

        {/* Properties Section */}
        {properties && properties.length > 0 && (
          <div className="nc-SectionGridFeaturePlaces relative">
            <Heading
              desc={`A ${data?.borgo?.name} ci sono ${properties?.length || 0} strutture disponibili`}
            >
              {`${t("translation:borghi.borgoPage.homesAt")} ${data?.borgo?.name}`}
            </Heading>
            <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {isLoadingProperties ? (
                <Skeleton count={4} height={300} />
              ) : (
                (showAllProperties ? properties : properties.slice(0, 8)).map(renderPropertyCard)
              )}
            </div>
            {properties.length > 8 && (
              <div className="flex justify-center mt-8">
                <ButtonSecondary onClick={() => setShowAllProperties(!showAllProperties)}>
                  {showAllProperties ? "Mostra meno" : `Mostra tutti i ${properties.length} alloggi`}
                </ButtonSecondary>
              </div>
            )}
          </div>
        )}

        {/* Experiences Section */}
        {experiences && experiences.length > 0 && (
          <div className="nc-SectionGridFeatureExperiences relative">
            <Heading
              desc={`A ${data?.borgo?.name} ci sono ${experiences?.length || 0} esperienze disponibili`}
            >
              {`Esperienze a ${data?.borgo?.name}`}
            </Heading>
            <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {isLoadingExperiences ? (
                <Skeleton count={4} height={300} />
              ) : (
                (showAllExperiences ? experiences : experiences.slice(0, 8)).map(renderExperienceCard)
              )}
            </div>
            {experiences.length > 8 && (
              <div className="flex justify-center mt-8">
                <ButtonSecondary onClick={() => setShowAllExperiences(!showAllExperiences)}>
                  {showAllExperiences ? "Mostra meno" : `Mostra tutte le ${experiences.length} esperienze`}
                </ButtonSecondary>
              </div>
            )}
          </div>
        )}

        {/* Holiday Packages Section */}
        {holidayPackages && holidayPackages.length > 0 && (
          <div className="nc-SectionGridFeaturePackages relative">
            <Heading
              desc={`A ${data?.borgo?.name} ci sono ${holidayPackages?.length || 0} pacchetti disponibili`}
            >
              {`Pacchetti a ${data?.borgo?.name}`}
            </Heading>
            <div className="grid gap-6 md:gap-8 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {isLoadingHolidayPackages ? (
                <Skeleton count={4} height={300} />
              ) : (
                (showAllPackages ? holidayPackages : holidayPackages.slice(0, 8)).map(renderHolidayPackageCard)
              )}
            </div>
            {holidayPackages.length > 8 && (
              <div className="flex justify-center mt-8">
                <ButtonSecondary onClick={() => setShowAllPackages(!showAllPackages)}>
                  {showAllPackages ? "Mostra meno" : `Mostra tutti i ${holidayPackages.length} pacchetti`}
                </ButtonSecondary>
              </div>
            )}
          </div>
        )}

        <SectionStatistic borgo={data?.borgo || {}} />
        {
          // map sections except the first one
          sections && sections.sections.slice(1).map((section: any, index: number) => {
            return (
              <SectionFounder
                title={section.title}
                description={section.description}
                image={section.cover}
                key={section.section_uuid}
                index={index}
              />
            );
          })
        }
        <SectionSubscribe2 />
      </div>
    </div>
  );
};

export default PageBorgo;
